<template>
    <!-- BEGIN FOOTER -->
    <div class="footer-wrapper">
        <div class="footer-section f-section-1">
            <p class="">
                Copyright © 2023 
                <a target="_blank" href="https://candidonlinemarketing.com">Candid Online Marketing</a>, 
                All rights reserved.
                <span class="app-version" v-if="appVersion" :title="versionTooltip" @click="showVersionInfo">
                    v{{ appVersion }}
                </span>
            </p>
        </div>
        <div class="footer-section f-section-2">
            <p class="">
                Coded with
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="feather feather-heart"
                >
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                </svg>
            </p>
        </div>
    </div>
    <!-- END FOOTER -->
</template>

<script>
export default {
    data() {
        return {
            appVersion: null,
            buildTime: null,
            isServiceWorkerActive: false,
            lastUpdateCheck: null,
            debugTapCount: 0,
            debugTapTimer: null
        };
    },
    computed: {
        versionTooltip() {
            let tooltip = '';
            
            if (this.buildTime) {
                try {
                    const buildDate = new Date(this.buildTime);
                    tooltip += `Built: ${buildDate.toLocaleString()}`;
                } catch (e) {
                    tooltip += 'Build time unknown';
                }
            }
            
            if (this.isServiceWorkerActive) {
                tooltip += '\n✅ PWA Active';
            }
            
            if (this.lastUpdateCheck) {
                tooltip += `\nLast check: ${this.lastUpdateCheck}`;
            }
            
            return tooltip || 'Click for version info';
        },
        
        buildTimeFormatted() {
            if (!this.buildTime) return '';
            try {
                return new Date(this.buildTime).toLocaleString();
            } catch (e) {
                return 'Build time unknown';
            }
        }
    },
    mounted() {
        console.log('Footer mounted - Version:', process.env.VUE_APP_VERSION, 'Build time:', process.env.VUE_APP_BUILD_TIME);
        
        // Set initial version from build-time environment
        this.appVersion = process.env.VUE_APP_VERSION;
        this.buildTime = process.env.VUE_APP_BUILD_TIME;
        
        // Check for cached version info (from previous SW messages)
        this.loadCachedVersionInfo();
        
        // Listen for service worker version updates
        this.setupVersionListeners();
        
        // Check service worker status
        this.checkServiceWorkerStatus();
        
        // Request current version from active service worker
        this.requestVersionFromServiceWorker();
    },
    methods: {
        loadCachedVersionInfo() {
            const cachedVersion = localStorage.getItem('app_version');
            const cachedBuildTime = localStorage.getItem('app_build_time');
            
            if (cachedVersion) {
                console.log('📋 Loading cached version:', cachedVersion);
                this.appVersion = cachedVersion;
            }
            if (cachedBuildTime) {
                this.buildTime = cachedBuildTime;
            }
        },
        
        setupVersionListeners() {
            // Listen for service worker updates
            window.addEventListener('sw-updated', (event) => {
                console.log('🎉 Footer received SW update:', event.detail);
                this.appVersion = event.detail.version;
                this.buildTime = event.detail.buildTime;
                this.lastUpdateCheck = new Date().toLocaleTimeString();
            });
            
            // Listen for version ready events
            window.addEventListener('sw-version-ready', (event) => {
                console.log('📋 Footer received SW version:', event.detail);
                this.appVersion = event.detail.version;
                this.buildTime = event.detail.buildTime;
            });
        },
        
        checkServiceWorkerStatus() {
            if ('serviceWorker' in navigator) {
                navigator.serviceWorker.ready.then(registration => {
                    this.isServiceWorkerActive = !!registration.active;
                    console.log('🔧 Service Worker status:', this.isServiceWorkerActive ? 'Active' : 'Inactive');
                });
            }
        },
        
        requestVersionFromServiceWorker() {
            if ('serviceWorker' in navigator) {
                navigator.serviceWorker.ready.then(registration => {
                    if (registration.active) {
                        const messageChannel = new MessageChannel();
                        messageChannel.port1.onmessage = (event) => {
                            if (event.data && event.data.version) {
                                console.log('📨 Footer received version from SW:', event.data.version);
                                this.appVersion = event.data.version;
                                this.buildTime = event.data.buildTime;
                            }
                        };
                        
                        registration.active.postMessage(
                            { type: 'GET_VERSION' },
                            [messageChannel.port2]
                        );
                    }
                });
            }
        },
        
        showVersionInfo() {
            const versionInfo = {
                version: this.appVersion || 'Unknown',
                buildTime: this.buildTimeFormatted || 'Unknown',
                serviceWorker: this.isServiceWorkerActive ? 'Active' : 'Inactive',
                environment: process.env.NODE_ENV || 'Unknown',
                lastCheck: this.lastUpdateCheck || 'Never'
            };
            
            if (this.$swal) {
                this.$swal({
                    title: 'App Version Info',
                    html: `
                        <div style="text-align: left; font-family: monospace; font-size: 12px;">
                            <p><strong>Version:</strong> ${versionInfo.version}</p>
                            <p><strong>Built:</strong> ${versionInfo.buildTime}</p>
                            <p><strong>PWA Status:</strong> ${versionInfo.serviceWorker}</p>
                            <p><strong>Environment:</strong> ${versionInfo.environment}</p>
                            <p><strong>Last Update Check:</strong> ${versionInfo.lastCheck}</p>
                        </div>
                    `,
                    icon: 'info',
                    confirmButtonText: 'OK',
                    footer: '<small>Click version number in footer anytime</small>'
                });
            } else {
                // Fallback for when SweetAlert isn't available
                alert(`App Version: ${versionInfo.version}\nBuilt: ${versionInfo.buildTime}\nPWA: ${versionInfo.serviceWorker}`);
            }
        }
    }
};
</script>

<style scoped>
.app-version {
    font-size: 0.8em;
    color: #888;
    margin-left: 8px;
    padding: 2px 6px;
    background-color: rgba(0,0,0,0.1);
    border-radius: 3px;
    cursor: pointer;
    transition: all 0.2s ease;
    user-select: none;
}

.app-version:hover {
    background-color: rgba(0,0,0,0.2);
    color: #333;
    transform: scale(1.05);
}

.app-version:active {
    transform: scale(0.95);
}

/* PWA-specific styles */
@media (display-mode: standalone) {
    .app-version {
        border: 1px solid rgba(0,0,0,0.2);
    }
    
    .app-version:hover {
        border-color: rgba(0,0,0,0.4);
    }
}
</style>
